<script>
import { defineComponent } from 'vue';
import Input from '@/components/Input.vue';

export default defineComponent({
    name: 'ProductCustomisationVariationAlphanumericInput',
    components: { Input },
    data() {
        return {
            cleanText: '',
            selectedColour: null,
        };
    },
    props: {
        variationType: {
            type: Object,
            required: true,
        },
    },
    computed: {
        text: {
            get() {
                return this.cleanText;
            },
            set(value) {
                const initialValue = this.cleanText;

                if (this.variationType.allow_letters) {
                    this.cleanText = value.replace(/[^a-zA-Z0-9\s]/g, '');
                } else {
                    this.cleanText = value.replace(/[^0-9]/g, '');
                }

                if (this.variationType.limit) {
                    this.cleanText = this.cleanText.slice(0, this.variationType.limit);
                }

                if (this.cleanText !== initialValue) {
                    this.updateSelectedVariations();
                }
            },
        },
        selectedVariations() {
            return [...this.text].map((char) => {
                const selectedVariation = this.variationType.variations.find((variation) => {
                    const namesMatch = variation.name.toLowerCase() === char.toLowerCase();

                    return this.displayColourOptions
                        ? this.selectedColour && namesMatch && variation.colour?.id === this.selectedColour
                        : namesMatch;
                });

                return selectedVariation || null;
            }).filter((variation) => variation);
        },
        selectedVariationsAreInStock() {
            return this.selectedVariations.every((variation) => variation.is_in_stock);
        },
        selectedOutOfStockVariations() {
            return this.selectedVariations.filter((variation) => !variation.is_in_stock);
        },
        displayColourOptions() {
            return this.variationType.colours?.length > 1;
        },
    },
    methods: {
        updateSelectedVariations() {
            this.$emit('update:selected', {
                ids: this.selectedVariations.map((variation) => variation.id),
                isText: false,
            });
        },
        selectColour(colourId) {
            this.selectedColour = colourId;
            this.updateSelectedVariations();
        },
    },
});
</script>

<template>
    <div>
        <Input
            v-model="text"
            :label="variationType.name"
            class="mb-2"
            :maxlength="variationType.limit"
            :type="variationType.allow_letters ? 'text' : 'number'"
        />

        <p
            v-if="variationType.limit"
            class="mb-2"
        >
            Maximum {{ variationType.limit }} characters
        </p>

        <div
            v-if="displayColourOptions"
            class="my-3 flex items-center flex-wrap gap-2"
        >
            <button
                v-for="(colour, index) in variationType.colours"
                :key="index"
                @click="selectColour(colour.id)"
            >
                <img
                    :src="colour.image"
                    class="rounded-full w-12 h-12 p-1 transition-shadow"
                    :class="selectedColour === colour.id ? 'ring-pink-500 ring-2' : 'ring-grey-lighter ring-1 hover:ring-grey-light hover:ring-2'"
                    :alt="colour.name"
                >
            </button>
        </div>

        <div
            v-if="selectedOutOfStockVariations.length > 0"
            class="mb-2"
        >
            <p class="text-red-light">
                The following characters are out of stock: {{ selectedOutOfStockVariations.map((variation) => variation.name).join(', ') }}
            </p>
        </div>

        <div class="grid grid-cols-12 gap-2">
            <div
                v-for="(variation, index) in selectedVariations"
                :key="`${variation.id}-${index}`"
                class="aspect-square relative rounded"
                :class="{
                    'bg-red-light': !variation.is_in_stock,
                }"
            >
                <img
                    v-if="variation.image.sizes.small"
                    :src="variation.image.sizes.small"
                    alt=""
                    class="absolute inset-0 object-cover rounded"
                    :class="{
                        'opacity-50': !variation.is_in_stock,
                    }"
                >
            </div>
        </div>
    </div>

</template>

<style scoped lang="scss">

</style>
